// Category picker (plan.md §4.2).
//
// With ~120 fixed categories, search is the primary path and browsing is the
// fallback — typing "bin" must surface Overflowing public bin, Damaged public
// bin, Dumped bin bags and Missed bin collection together so the user chooses
// between them.
//
// There is no "add your own": the taxonomy is fixed (plan.md §3). An
// unmatched search suggests the nearest collection's "…: other" rather than
// dead-ending, and is recorded so real gaps surface from usage (OQ4).

import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { iconFor } from '../lib/icons.js';
import { logPickerMiss } from '../lib/db.js';

/**
 * Client-side ranking that mirrors what the trigram index would do, without a
 * round trip per keystroke. The whole taxonomy is already loaded (a few KB),
 * so searching locally is both faster and cheaper.
 */
function scoreCategory(category, collection, needle) {
  const name = category.name.toLowerCase();
  const words = name.split(/[\s:,]+/);

  if (name === needle) return 1000;
  if (name.startsWith(needle)) return 900 - name.length;
  if (words.some((w) => w.startsWith(needle))) return 800 - name.length;
  if (name.includes(needle)) return 700 - name.length;
  // Collection name matches let "waste" surface the whole waste collection.
  if (collection.name.toLowerCase().includes(needle)) return 300 - name.length;

  // Cheap fuzzy pass so "potholee" and "pothol" both still find Pothole.
  let i = 0;
  for (const char of name) if (char === needle[i]) i += 1;
  if (i === needle.length) return 200 - name.length;

  return 0;
}

export default function CategoryPicker({ taxonomy, onPick, onCancel, autoFocus = true }) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [openCollection, setOpenCollection] = useState(null);
  const inputRef = useRef(null);
  // Records the search term alongside the pick, so a miss followed by an
  // "…: other" selection is attributable to the term that failed.
  const lastUnmatchedQuery = useRef(null);

  useEffect(() => {
    if (autoFocus) inputRef.current?.focus();
  }, [autoFocus]);

  const needle = query.trim().toLowerCase();

  const results = useMemo(() => {
    if (needle.length < 2) return null;

    const scored = [];
    for (const collection of taxonomy) {
      for (const category of collection.categories) {
        const score = scoreCategory(category, collection, needle);
        if (score > 0) scored.push({ category, collection, score });
      }
    }
    scored.sort((a, b) => b.score - a.score);

    // Group under collection headers, preserving the best-match ordering.
    const groups = new Map();
    for (const hit of scored.slice(0, 40)) {
      if (!groups.has(hit.collection.id)) groups.set(hit.collection.id, { collection: hit.collection, items: [] });
      groups.get(hit.collection.id).items.push(hit.category);
    }
    return [...groups.values()];
  }, [needle, taxonomy]);

  const hasResults = results && results.length > 0;

  useEffect(() => {
    if (needle.length >= 3 && results && results.length === 0) {
      lastUnmatchedQuery.current = needle;
    }
  }, [needle, results]);

  function pick(category, collection) {
    // Only log when this selection followed a search that found nothing, and
    // resolved to an "…: other" — that pair is the actual gap signal (OQ4).
    if (lastUnmatchedQuery.current && category.is_other) {
      logPickerMiss(lastUnmatchedQuery.current, category.id);
    }
    onPick({ category, collection });
  }

  // The suggested fallbacks when nothing matched: every collection's
  // "…: other", so there is always somewhere to go.
  const otherCategories = useMemo(
    () => taxonomy
      .map((collection) => ({
        collection,
        category: collection.categories.find((c) => c.is_other),
      }))
      .filter((entry) => entry.category),
    [taxonomy]);

  return (
    <div className="picker">
      <div className="picker__search">
        <input
          ref={inputRef}
          type="search"
          className="picker__input"
          placeholder={t('picker.placeholder')}
          aria-label={t('picker.aria')}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          autoComplete="off"
          enterKeyHint="search"
        />
        {onCancel && (
          <button type="button" className="btn btn--ghost" onClick={onCancel}>{t('picker.cancel')}</button>
        )}
      </div>

      <div className="picker__results" role="listbox" aria-label={t('picker.listbox')}>
        {/* Search results */}
        {hasResults && results.map(({ collection, items }) => (
          <section key={collection.id} className="picker__group">
            <h3 className="picker__group-title">
              <span aria-hidden="true">{iconFor(collection.icon)}</span> {collection.name}
            </h3>
            {items.map((category) => (
              <button
                key={category.id}
                type="button"
                role="option"
                aria-selected="false"
                className="picker__item"
                onClick={() => pick(category, collection)}
              >
                <span className="picker__item-icon" aria-hidden="true">
                  {iconFor(category.icon, collection.icon)}
                </span>
                <span className="picker__item-name">{category.name}</span>
                {collection.location_precision_m > 0 && (
                  <span className="picker__item-note" title={t('picker.approx')}>
                    {t('picker.approxLabel', { m: collection.location_precision_m })}
                  </span>
                )}
              </button>
            ))}
          </section>
        ))}

        {/* Unmatched search: suggest the "other" entries rather than dead-end */}
        {results && results.length === 0 && (
          <div className="picker__empty">
            <p className="picker__empty-title">{t('picker.emptyTitle', { query: query.trim() })}</p>
            <p className="picker__empty-body">
              {t('picker.emptyBody')}
            </p>
            {otherCategories.map(({ collection, category }) => (
              <button
                key={category.id}
                type="button"
                className="picker__item"
                onClick={() => pick(category, collection)}
              >
                <span className="picker__item-icon" aria-hidden="true">{iconFor(collection.icon)}</span>
                <span className="picker__item-name">{category.name}</span>
              </button>
            ))}
          </div>
        )}

        {/* Browse, for people who would rather scroll than type */}
        {!results && taxonomy.map((collection) => {
          const isOpen = openCollection === collection.id;
          return (
            <section key={collection.id} className="picker__group">
              <button
                type="button"
                className="picker__collection"
                aria-expanded={isOpen}
                onClick={() => setOpenCollection(isOpen ? null : collection.id)}
              >
                <span className="picker__item-icon" aria-hidden="true">{iconFor(collection.icon)}</span>
                <span className="picker__item-name">{collection.name}</span>
                <span className="picker__item-note">
                  {collection.categories.length}
                  <span className="picker__chevron" data-open={isOpen}>›</span>
                </span>
              </button>

              {isOpen && collection.categories.map((category) => (
                <button
                  key={category.id}
                  type="button"
                  className="picker__item picker__item--nested"
                  onClick={() => pick(category, collection)}
                >
                  <span className="picker__item-icon" aria-hidden="true">
                    {iconFor(category.icon, collection.icon)}
                  </span>
                  <span className="picker__item-name">{category.name}</span>
                </button>
              ))}
            </section>
          );
        })}
      </div>
    </div>
  );
}
